"use client"

import {React, useState, useEffect} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ChevronDown, GroupIcon, Link2, LinkIcon, UserRoundCog } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {  Settings, LogOut } from 'lucide-react';
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

const amplyyLogo = new URL('../assets/amplyy.png', import.meta.url).href;

const { FiHome, FiUsers, FiMail, FiLink, FiBarChart3, FiMusic, FiSun, FiMoon, FiLogOut } = FiIcons;

const Sidebar = () => {
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const { isDark, toggleTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false)
  const { user } = useAuth();
  const [currentUser, setCurrentUser] = useState(null)

  // Fetch user profile, subscription, and plan
    useEffect(() => {
      if (!user) return;
      Promise.all([
        supabase.from('profiles').select('full_name, email, role').eq('id', user.id).maybeSingle(),
        supabase.from('subscriptions').select('plan_id, status, session_id, customer_id').eq('user_id', user.id).maybeSingle()
      ]).then(async ([{ data: profileData }]) => {
        setProfile(profileData);
      });
    }, [user]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('user')
      if (raw) {
        setCurrentUser(JSON.parse(raw))
      }
    } catch (e) {
      console.warn('Failed to parse stored user:', e)
    }
  }, [])



  const menuItems = [
    { path: '/dashboard', icon: FiHome, label: 'Dashboard' },
    { path: '/create-campaign', icon: FiIcons.FiPlus, label: 'Create Campaign' },
    { path: '/smart-links', icon: FiLink, label: 'Smart Links' },
    { path: '/fans', icon: FiUsers, label: 'My Fans' },
    { path: '/guests', icon: UserRoundCog, label: 'My Guests' },
    { path: '/campaigns', icon: FiMail, label: 'My Campaigns' },
    { path: '/affiliates', icon: LinkIcon, label: 'Affiliates' }
  ];

  const handleLogout = async () => {
    toast.success('Logging out ...');
    setTimeout(async() => {
    await supabase.auth.signOut();
    window.location.href = './login';
  }, 2000);
  };

  return (
  <div className="fixed left-0 top-0 h-full w-64 bg-white dark:bg-black border-r border-slate-200 dark:border-gray-700 shadow-sm hidden md:block">
      <div className="p-6 border-b border-slate-200 dark:border-gray-700">
        <a href="/dashboard">
        <div className="flex flex-col items-center justify-center">
          <img src={amplyyLogo} alt="Logo" className="h-[80px] w-[150px] mx-auto" />
        </div>
        </a>
      </div>

      <nav className="p-4">
        <ul className="space-y-2">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <li key={item.path}>
                <Link to={item.path}>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-gradient-to-r from-[#e3c5b3] to-[#e3c5b3] text-white shadow-lg'
                        : 'text-black dark:text-slate-300 hover:bg-[#e3c5b3] dark:hover:bg-[#e3c5b3] hover:text-white dark:hover:text-white'
                    }`}
                  >
                    <SafeIcon icon={item.icon} className="text-lg" />
                    <span className="font-medium">{item.label}</span>
                  </motion.div>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="absolute bottom-6 left-4 right-4 space-y-3">
        <div className="flex items-center space-x-4">
          <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center space-x-2 w-full text-left hover:bg-accent/50 rounded-lg p-2 transition-colors">
                <Avatar className="w-8 h-8">
                  <AvatarImage src="/placeholder.svg?height=32&width=32" />
                  <AvatarFallback className="text-black dark:text-white">{profile?.full_name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div className="text-sm flex-1">
                  <div className="font-medium text-gray-900 dark:text-gray-100">{profile?.full_name || profile?.name || "User"}</div>
                  <div style={{ fontWeight: "bold" }} className="text-[#ebcebc]">
                    {profile?.role ? profile.role.charAt(0).toUpperCase() + profile.role.slice(1) : 'Artist'}
                  </div>
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-gray-400 dark:text-white transition-transform duration-200 ${
                    isOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56 mb-2" side="top">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none text-black dark:text-white">{profile?.full_name || profile?.name || "User"}</p>
                  <p className="text-xs leading-none text-muted-foreground text-black dark:text-white">{profile?.email || '—'}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <a href='/settings'>
                <DropdownMenuItem className="cursor-pointer">
                  <Settings className="text-black dark:text-white mr-2 h-4 w-4" />
                  <span className='text-black dark:text-white'>Account Settings</span>
                </DropdownMenuItem>
              </a>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={toggleTheme} className="cursor-pointer flex items-center gap-2">
                <SafeIcon icon={isDark ? FiSun : FiMoon} className="text-black dark:text-white text-lg" />
                <span className="text-black dark:text-white">{isDark ? 'Light Mode' : 'Dark Mode'}</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="cursor-pointer text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400"
              >
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;